import { Audit, AuditAction, AuditDomain, NoteDetails } from 'models/Audit';
import { getUIAppointmentStatus } from 'utils/jobStatus';
import { camelToTitleCase, capitalise } from 'utils';
import { DateTime } from 'luxon';
import { DATE_WITH_SLASH_FORMAT, scheduleTimezone } from 'constants/dates';
import { AppointmentStatus } from 'models/Appointment';
import { AUDIT_SYSTEM_USER } from 'constants/audit';
import { JobMovementEngineerInfo } from 'models/Engineers';

export interface AuditRow {
  action: string;
  actionedBy: string;
  time: string;
}

function formatFieldName(name: string): string {
  return camelToTitleCase(name);
}

function formatFieldValue(
  value: string | null,
  changedField: string | null
): string {
  if (value == null) {
    return 'null';
  }
  if (changedField === 'status') {
    return getUIAppointmentStatus((AppointmentStatus as any)[value]);
  }
  if (changedField === 'date') {
    return DateTime.fromISO(value).toFormat(DATE_WITH_SLASH_FORMAT);
  }
  return value;
}

function formattedTime(timestamp: number): string {
  return DateTime.fromMillis(timestamp, {
    zone: scheduleTimezone
  }).toFormat('ccc d LLL yyyy HH:mm');
}

function getFriendlyEntityName(domain: AuditDomain): string {
  switch (domain) {
    case AuditDomain.APPOINTMENT: {
      return 'Job';
    }
    default: {
      return formatFieldName(domain.toString());
    }
  }
}

function createActionSentence(auditEntry: Audit): string {
  if (auditEntry.changedField) {
    return `${formatFieldName(auditEntry.changedField)} created`;
  }
  return `${getFriendlyEntityName(auditEntry.domain)} created`;
}

function engineerNameOrUnassigned(
  auditValue: string | null,
  engineers?: JobMovementEngineerInfo[]
) {
  if (!auditValue) {
    return 'Unassigned';
  }

  const engineer = engineers?.find(({ id }) => id === auditValue);

  if (engineer == undefined) {
    return 'Unknown';
  }

  return `${engineer.firstName} ${engineer.lastName}`;
}

function updateActionSentence(auditEntry: Audit): string {
  if (!auditEntry.changedField) {
    return `${getFriendlyEntityName(auditEntry.domain)} updated`;
  }
  if (auditEntry.changedFrom || auditEntry.changedTo) {
    return `${formatFieldName(
      auditEntry.changedField
    )} updated from ${formatFieldValue(
      auditEntry.changedFrom,
      auditEntry.changedField
    )} to ${formatFieldValue(auditEntry.changedTo, auditEntry.changedField)}`;
  }

  return `${formatFieldName(auditEntry.changedField)} updated`;
}

function createAppointmentNoteSentence(auditEntry: Audit): string {
  switch (auditEntry.action) {
    case AuditAction.CREATE: {
      if (auditEntry.changedTo) {
        const noteDetails: NoteDetails = JSON.parse(auditEntry.changedTo);
        if (noteDetails.type && noteDetails.note) {
          return `${capitalise(noteDetails.type.toLowerCase())} note added: ${
            noteDetails.note
          }`;
        }
      }
      return 'Note added';
    }

    case AuditAction.DELETE: {
      if (auditEntry.changedFrom) {
        const noteDetails: NoteDetails = JSON.parse(auditEntry.changedFrom);
        if (noteDetails.type && noteDetails.note) {
          return `${capitalise(noteDetails.type.toLowerCase())} note deleted: ${
            noteDetails.note
          }`;
        }
      }
      return 'Note deleted';
    }
    default: {
      return 'Unknown Note Action';
    }
  }
}

function createJobMovementNoteSentence(
  auditEntry: Audit,
  engineers?: JobMovementEngineerInfo[]
) {
  return `Job assignment updated from ${engineerNameOrUnassigned(
    auditEntry.changedFrom,
    engineers
  )} to ${engineerNameOrUnassigned(auditEntry.changedTo, engineers)}`;
}

function actionSentence(
  auditEntry: Audit,
  engineers?: JobMovementEngineerInfo[]
): string {
  if (auditEntry.changedField === 'notes') {
    return createAppointmentNoteSentence(auditEntry);
  }
  if (auditEntry.changedField === 'scheduleId') {
    return createJobMovementNoteSentence(auditEntry, engineers);
  }
  switch (auditEntry.action) {
    case AuditAction.CREATE: {
      return createActionSentence(auditEntry);
    }
    case AuditAction.DELETE: {
      return `${getFriendlyEntityName(auditEntry.domain)} deleted`;
    }
    case AuditAction.UPDATE: {
      return updateActionSentence(auditEntry);
    }
    default: {
      return 'Unknown Action';
    }
  }
}

function changedByName(auditEntry: Audit): string {
  const { username, firstName, lastName } = auditEntry.changedBy;
  if (username === AUDIT_SYSTEM_USER) {
    return 'System';
  }
  if (!firstName && !lastName) {
    return 'Unknown';
  }
  return `${firstName?.trim() || ''} ${lastName?.trim() || ''}`.trim();
}

function auditEntryToRow(
  auditEntry: Audit,
  engineers?: JobMovementEngineerInfo[]
): AuditRow {
  return {
    action: actionSentence(auditEntry, engineers),
    actionedBy: changedByName(auditEntry),
    time: formattedTime(auditEntry.timestamp)
  };
}

function mostRecentFirst(...auditItems: Audit[]): number {
  const [aDateTime, bDateTime] = auditItems.map(({ timestamp }) => timestamp);
  return bDateTime - aDateTime;
}

function groupBy<T, K>(arr: T[], key: (i: T) => K): Map<K, T[]> {
  const groups: Map<K, T[]> = new Map<K, T[]>();
  arr.forEach(element => {
    let list = groups.get(key(element));
    if (list === undefined) {
      list = [];
      list.push(element);
      groups.set(key(element), list);
    } else {
      list.push(element);
    }
  });
  return groups;
}

const ASSIGNMENT_PAIRING_WINDOW_MS = 100;

const isSystemUser = (audit: Audit): boolean =>
  audit.changedBy.username === AUDIT_SYSTEM_USER;

// At equal timestamps the DELETE happens first: both rows are written in the same transaction
const actionOrder = (audit: Audit): number =>
  audit.action === AuditAction.DELETE ? 0 : 1;

// The API returns newest first, so reverse before sorting to keep equal-timestamp ties stable
function oldestFirst(scheduleAudits: Audit[]): Audit[] {
  return [...scheduleAudits]
    .reverse()
    .sort(
      (a, b) => a.timestamp - b.timestamp || actionOrder(a) - actionOrder(b)
    );
}

function moveAudit(deleteAudit: Audit, createAudit: Audit): Audit {
  // Until deletes are attributed correctly upstream, the DELETE may carry a stale System user
  const actionedBy =
    isSystemUser(createAudit) && !isSystemUser(deleteAudit)
      ? deleteAudit
      : createAudit;
  return {
    ...createAudit,
    action: AuditAction.UPDATE,
    changedFrom: deleteAudit.additionalData,
    changedTo: createAudit.additionalData,
    changedBy: actionedBy.changedBy,
    timestamp: Math.max(deleteAudit.timestamp, createAudit.timestamp)
  };
}

const unassignedAudit = (audit: Audit): Audit => ({
  ...audit,
  changedFrom: audit.additionalData
});

const assignedAudit = (audit: Audit): Audit => ({
  ...audit,
  changedTo: audit.additionalData
});

const releasedAudit = (audit: Audit): Audit =>
  audit.action === AuditAction.CREATE
    ? assignedAudit(audit)
    : unassignedAudit(audit);

// Walks the audits in order, holding each one until we know whether the next one closely follows it to make a move
function updateScheduleAudits(scheduleAudits: Audit[]): Audit[] {
  const result: Audit[] = [];
  let pendingAudit: Audit | undefined;

  oldestFirst(scheduleAudits).forEach(audit => {
    if (
      pendingAudit &&
      audit.timestamp - pendingAudit.timestamp > ASSIGNMENT_PAIRING_WINDOW_MS
    ) {
      result.push(releasedAudit(pendingAudit));
      pendingAudit = undefined;
    }

    const isSameEngineer =
      pendingAudit?.additionalData === audit.additionalData;

    if (!pendingAudit) {
      pendingAudit = audit;
    } else if (
      pendingAudit.action === AuditAction.DELETE &&
      audit.action === AuditAction.CREATE
    ) {
      // Unassigned and reassigned to the same engineer is optimiser noise we can ignore
      if (!isSameEngineer) {
        result.push(moveAudit(pendingAudit, audit));
      }
      pendingAudit = undefined;
    } else if (
      pendingAudit.action === AuditAction.CREATE &&
      audit.action === AuditAction.DELETE &&
      !isSameEngineer
    ) {
      // Scheduling adds a job to the new engineer and removes it from the old one in parallel, so the assign can come first
      result.push(moveAudit(audit, pendingAudit));
      pendingAudit = undefined;
    } else {
      result.push(releasedAudit(pendingAudit));
      pendingAudit = audit;
    }
  });

  if (pendingAudit) {
    result.push(releasedAudit(pendingAudit));
  }

  return result.reverse();
}

export function auditEntriesToRows(
  auditEntries: Audit[],
  engineers?: JobMovementEngineerInfo[]
): AuditRow[] {
  const auditsByType = groupBy(auditEntries, audit => audit.changedField);

  const scheduleAudits = auditsByType.get('scheduleId');
  if (scheduleAudits !== undefined) {
    auditsByType.set('scheduleId', updateScheduleAudits(scheduleAudits));
  }

  return [...Array.from(auditsByType.values())]
    .flat()
    .sort(mostRecentFirst)
    .map(entry => auditEntryToRow(entry, engineers));
}
